import React, { createContext, useState, useEffect, useContext } from 'react';
import { API_BASE_URL } from '../config';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
    const [userToken, setUserToken] = useState(null);
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [userName, setUserName] = useState('');
    const [queriesUsed, setQueriesUsed] = useState(0);
    const [queriesRemaining, setQueriesRemaining] = useState(999); // Default to high number
    const [isBlocked, setIsBlocked] = useState(false);
    const [loading, setLoading] = useState(true);

    const checkUserStatus = async () => {
        try {
            // Get token from localStorage if available
            const token = localStorage.getItem('userToken');
            const headers = {};
            if (token && token.includes('.')) {
                headers['Authorization'] = `Bearer ${token}`;
            }

            // Credentials 'include' ensures the browser sends the httpOnly cookie
            const response = await fetch(`${API_BASE_URL}/auth/status`, {
                credentials: 'include',
                headers: headers
            });
            const data = await response.json();

            if (response.ok) {
                if (data.authenticated) {
                    setQueriesUsed(data.queries_used);
                    setQueriesRemaining(data.queries_remaining);
                    // Force unblocked if in unlimited mode (backend sends 9999)
                    setIsBlocked(data.queries_remaining > 500 ? false : data.is_blocked);

                    setIsAuthenticated(true);
                    if (token && token.includes('.')) {
                        setUserToken(token);
                    }
                    return true;
                } else {
                    setIsAuthenticated(false);
                    setUserToken(null);
                    return false;
                }
            } else {
                setIsAuthenticated(false);
                setUserToken(null);
                return false;
            }
        } catch (error) {
            console.error('Failed to check user status:', error);
            return false;
        }
    };

    useEffect(() => {
        const name = localStorage.getItem('userName');
        if (name) setUserName(name);

        // Check session via cookie on mount
        checkUserStatus().finally(() => {
            setLoading(false);
        });
    }, []);

    const login = (userData) => {
        // Token is now set in httpOnly cookie by the backend
        // We also store it in localStorage for cross-site header support
        if (userData.token) {
            localStorage.setItem('userToken', userData.token);
            setUserToken(userData.token);
        }

        setIsAuthenticated(true);
        setUserName(userData.name);
        setQueriesRemaining(userData.queries_remaining);
        setQueriesUsed(userData.queries_used || 0);

        localStorage.setItem('userName', userData.name);
        localStorage.setItem('userEmail', userData.email || '');
    };

    const logout = async () => {
        try {
            await fetch(`${API_BASE_URL}/auth/logout`, {
                method: 'POST',
                credentials: 'include'
            });
        } catch (error) {
            console.error('Logout API call failed:', error);
        }

        localStorage.removeItem('userName');
        localStorage.removeItem('userEmail');
        localStorage.removeItem('userToken');

        setIsAuthenticated(false);
        setUserToken(null);
        setUserName('');
        setQueriesUsed(0);
        setQueriesRemaining(999);
    };

    const refreshStatus = () => {
        checkUserStatus();
    };

    const value = {
        userToken,
        isAuthenticated,
        userName,
        queriesUsed,
        queriesRemaining,
        isBlocked,
        loading,
        login,
        logout,
        refreshStatus,
        setIsBlocked
    };

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};
